import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTenant } from "@/lib/tenant";
import { usePrefs } from "@/lib/prefs";
import { get } from "@/lib/api";
import { useApi } from "@/lib/hooks";
import { subscribeTenantBus } from "@/lib/echo";
import { BRANDS, setBrandSideEffects, useBranding } from "@/lib/branding";
import { TourProvider } from "@/components/Tour";
import BillingBanner from "@/components/BillingBanner";
import { ImpersonationBanner } from "@/components/overlays/ImpersonationBanner";
import NotificationsPanel from "@/components/overlays/NotificationsPanel";
import CommandPalette from "@/components/overlays/CommandPalette";
import HelpPanel from "@/components/overlays/HelpPanel";
import KeyboardShortcutsOverlay from "@/components/overlays/KeyboardShortcutsOverlay";
import { bottomBarSlots, firstPath, type Area, type BadgeKey } from "./model";
import { areaJumps, palettePages } from "./palette";
import { useNav } from "./useNav";
import { useMediaQuery } from "./useMediaQuery";
import { Rail } from "./Rail";
import { PageList } from "./PageList";
import { PageTabs } from "./PageTabs";
import { TopBar } from "./TopBar";
import { BottomBar } from "./BottomBar";
import { AreaSheet } from "./AreaSheet";
import "./nav.css";

// Pages reached from the avatar menu or first-run flow, not the menu.
const OTHER_CRUMBS: Record<string, string> = {
  "/account": "Account",
  "/me/profile": "My profile",
  "/onboarding": "Onboarding",
};

type Sheet = { kind: "area"; areaId: string } | { kind: "more" } | null;

export function StaffShell() {
  const { prefs, toggleSidebar } = usePrefs();
  const { user } = useAuth();
  const { current } = useTenant();
  const loc = useLocation();
  const nav = useNavigate();
  const { areas, active, activeArea, isAdmin } = useNav();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const branding = useBranding();

  const [notifOpen, setNotifOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);

  const closeNav = useCallback(() => { setListOpen(false); setPreviewId(null); setSheet(null); }, []);
  useEffect(() => { closeNav(); }, [loc.pathname, closeNav]);

  const brand = BRANDS[activeArea?.brand ?? "control"];
  useEffect(() => { setBrandSideEffects(brand); }, [brand]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen((c) => !c); }
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
        if (tag !== "input" && tag !== "textarea" && !(e.target as HTMLElement | null)?.isContentEditable) {
          e.preventDefault();
          setHelpOpen((h) => !h);
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "/") { e.preventDefault(); setShortcutsOpen((s) => !s); }
      if (e.key === "Escape") {
        setCmdOpen(false); setNotifOpen(false); setHelpOpen(false); setShortcutsOpen(false);
        setListOpen(false); setPreviewId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // First-run redirect for the tenant's own owners/admins (FE-23: not super admins).
  const role = current?.pivot?.role ?? "";
  const canManage = role === "owner" || role === "admin";
  const cacheKey = current ? `control.onboarding.completed.${current.id}` : null;

  useEffect(() => {
    if (!cacheKey) return;
    const onComplete = () => { localStorage.setItem(cacheKey, "1"); };
    window.addEventListener("onboarding-completed", onComplete);
    return () => window.removeEventListener("onboarding-completed", onComplete);
  }, [cacheKey]);

  useEffect(() => {
    if (!current?.id) return;
    let off: (() => void) | null = null;
    let cancelled = false;
    subscribeTenantBus(current.id).then((unsub) => {
      if (cancelled) { unsub(); return; }
      off = unsub;
    });
    return () => { cancelled = true; if (off) off(); };
  }, [current?.id]);

  useEffect(() => {
    if (!user || !current || !canManage || !cacheKey) return;
    if (loc.pathname.startsWith("/onboarding") || loc.pathname.startsWith("/super")) return;
    if (localStorage.getItem(cacheKey) === "1") return;
    let cancelled = false;
    get<{ completed_at?: string | null }>("/v1/me/onboarding")
      .then((d) => {
        if (cancelled) return;
        if (d?.completed_at) localStorage.setItem(cacheKey, "1");
        else nav("/onboarding", { replace: true });
      })
      .catch(() => { /* best-effort; don't block the app */ });
    return () => { cancelled = true; };
  }, [user, current, canManage, cacheKey, loc.pathname, nav]);

  // One badge count per role: admins triage the inbox, everyone else their own tickets.
  const supportVisible = areas.some((a) => a.id === "support");
  const waterVisible = areas.some((a) => a.id === "water");
  const tickets = useApi<{ data?: { open_count?: number } }>(supportVisible ? "/v1/support/tickets/badge" : null);
  const ncs = useApi<{ summary?: { open?: number } }>(waterVisible ? "/v1/jcontrol/non-conformities?per_page=1" : null);
  const orphans = useApi<{ data?: { unmonitored?: number } }>(waterVisible ? "/v1/jcontrol/orphans/summary" : null);
  const openTickets = tickets.data?.data?.open_count || undefined;
  const badges: Partial<Record<BadgeKey, number>> = {
    "support-mine": isAdmin ? undefined : openTickets,
    "support-inbox": isAdmin ? openTickets : undefined,
    "water-ncs": ncs.data?.summary?.open || undefined,
    "water-orphans": orphans.data?.data?.unmonitored || undefined,
  };

  const pickArea = useCallback((a: Area) => {
    if (isDesktop) { setPreviewId(null); nav(firstPath(a)); return; }
    setPreviewId(a.id);
    setListOpen(true);
  }, [isDesktop, nav]);

  const listArea = areas.find((a) => a.id === previewId) ?? activeArea;
  const crumbs = active ? [active.area.label, active.item.label] : [OTHER_CRUMBS[loc.pathname]].filter((c): c is string => !!c);
  const pages = useMemo(() => palettePages(areas), [areas]);
  const jumps = useMemo(() => areaJumps(areas), [areas]);
  const sheetArea = sheet?.kind === "area" ? areas.find((a) => a.id === sheet.areaId) ?? null : null;

  return (
    <TourProvider userId={user?.id ?? null} autostart={!!user && !user.is_super_admin}>
      <div className="app-shell staff-shell">
        <Rail areas={areas} activeId={activeArea?.id ?? null}
          logoSrc={branding.logo_url ?? brand.logoPath} accent={brand.accentColor} onPick={pickArea} />
        {listArea && (
          <PageList area={listArea} activeItemId={active?.item.id ?? null} badges={badges}
            collapsed={prefs.sidebar === "collapsed"} open={listOpen} onToggle={toggleSidebar} onNavigate={closeNav} />
        )}
        {listOpen && <div className="list-scrim" onClick={closeNav} />}
        <div className="main">
          <ImpersonationBanner />
          <BillingBanner />
          <TopBar crumbs={crumbs} onNotif={() => setNotifOpen(true)} onCmd={() => setCmdOpen(true)} onHelp={() => setHelpOpen(true)} />
          <div className="content">
            <PageTabs item={active?.item ?? null} pathname={loc.pathname} />
            <Suspense fallback={<div className="content-inner"><div className="kicker">Loading…</div></div>}>
              <Outlet />
            </Suspense>
          </div>
        </div>
        <BottomBar areas={areas} activeId={activeArea?.id ?? null}
          onPick={(a) => setSheet({ kind: "area", areaId: a.id })} onMore={() => setSheet({ kind: "more" })} />
        {sheet && (
          <AreaSheet
            key={sheet.kind === "area" ? sheet.areaId : "more"}
            area={sheetArea}
            areas={sheet.kind === "more" ? bottomBarSlots(areas).more : []}
            activeItemId={active?.item.id ?? null}
            onPickArea={(a) => setSheet({ kind: "area", areaId: a.id })}
            onClose={() => setSheet(null)}
          />
        )}
        <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
        <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} pages={pages} jumps={jumps} />
        <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
        <KeyboardShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      </div>
    </TourProvider>
  );
}
